import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { Store, BookingSlot, StoreTier, StorePosition } from '../../data/types';
import { CalendarIcon, UsersIcon, ChevronDownIcon } from 'lucide-react';
import { buyers, getBuyerForBrand } from '../../data/buyers';
import { brands } from '../../data/brands';
interface DashboardCalendarProps {
  stores: Store[];
  brandName?: string;
}
type ViewMode = 'brand' | 'week';
const TIER_ORDER: StoreTier[] = ['flagship', 'high-traffic', 'standard'];
const TIER_LABELS: Record<StoreTier, string> = {
  flagship: 'FLAGSHIP',
  'high-traffic': 'HIGH TRAFFIC',
  standard: 'STANDARD'
};
// Buyer-based brand colour lookup
function getBrandColorClass(brandName: string): string {
  if (!brandName) return 'bg-white text-black';
  const buyer = getBuyerForBrand(brandName);
  if (buyer) return `${buyer.color} ${buyer.textColor}`;
  // Fallback for unknown brands
  return 'bg-gray-300 text-gray-800';
}
// Only 2 colors: outpost (red/magenta) and cylinder (green)
const POSITION_HEADER_COLORS = {
  outpost: 'bg-red-500 text-white',
  cylinder: 'bg-emerald-500 text-white'
};
const POSITION_BADGE_COLORS = {
  outpost: 'bg-red-100 text-red-700',
  cylinder: 'bg-emerald-100 text-emerald-700'
};
export function DashboardCalendar({
  stores,
  brandName
}: DashboardCalendarProps) {
  const isBrandView = !!brandName;
  const [viewMode, setViewMode] = useState<ViewMode>(isBrandView ? 'week' : 'week');
  const [selectedWeek, setSelectedWeek] = useState<number>(1);
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  // 1. Extract all unique weeks
  const weeks = useMemo(() => {
    const weekMap = new Map<number, {
      start: string;
      end: string;
      month: string;
    }>();
    stores.forEach(store => {
      store.slots.forEach(slot => {
        if (!weekMap.has(slot.weekNumber)) {
          const date = new Date(slot.weekStartDate);
          const month = date.toLocaleString('default', {
            month: 'long',
            year: 'numeric'
          }).toUpperCase();
          weekMap.set(slot.weekNumber, {
            start: slot.weekStartDate,
            end: slot.weekEndDate,
            month
          });
        }
      });
    });
    return Array.from(weekMap.entries()).map(([num, data]) => ({
      weekNumber: num,
      ...data
    })).sort((a, b) => a.weekNumber - b.weekNumber);
  }, [stores]);
  // 2. All brands from master list (sorted alphabetically)
  const allBrands = useMemo(() => {
    return brands.filter(b => b.category !== 'Internal').map(b => b.name).sort();
  }, []);
  // Initialize selectedBrand when brands list is first available
  useEffect(() => {
    if (allBrands.length > 0 && !selectedBrand) {
      setSelectedBrand(allBrands[0]);
    }
  }, [allBrands, selectedBrand]);
  // 3. Group stores by tier
  const storesByTier = useMemo(() => {
    const grouped: Record<StoreTier, Store[]> = {
      flagship: [],
      'high-traffic': [],
      standard: []
    };
    stores.forEach(store => {
      grouped[store.tier].push(store);
    });
    return grouped;
  }, [stores]);
  // 4. Generic position columns — compact 5-column layout
  const genericColumns = useMemo(() => [{
    key: 'screen-1',
    label: 'Screen 1',
    type: 'outpost' as const
  }, {
    key: 'screen-2',
    label: 'Screen 2',
    type: 'outpost' as const
  }, {
    key: 'screen-3',
    label: 'Screen 3',
    type: 'outpost' as const
  }, {
    key: 'screen-4',
    label: 'Screen 4',
    type: 'outpost' as const
  }, {
    key: 'drive-aisle',
    label: 'Drive Aisle',
    type: 'cylinder' as const
  }], []);
  // Map a store's real positions into generic column slots
  const getStoreSlotForColumn = (store: Store, columnKey: string, weekNumber: number): {
    slot?: BookingSlot;
    positionName?: string;
  } => {
    const positions = store.positions || [];
    let matchedPosition: StorePosition | undefined;
    if (columnKey.startsWith('screen-')) {
      const screenNum = columnKey.replace('screen-', '');
      matchedPosition = positions.find(p => p.name.startsWith(`Screen ${screenNum} `));
    } else if (columnKey === 'drive-aisle') {
      matchedPosition = positions.find(p => p.name.startsWith('Drive Aisle'));
    }
    if (!matchedPosition) return {};
    const slot = store.slots.find(s => s.positionId === matchedPosition!.id && s.weekNumber === weekNumber);
    return {
      slot,
      positionName: matchedPosition.name
    };
  };
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getDate()}/${d.getMonth() + 1}`;
  };
  const currentWeekData = weeks.find(w => w.weekNumber === selectedWeek);
  // Clean cell renderer — brand-aware
  const renderCell = (slot?: BookingSlot) => {
    if (!slot) return <div className="w-full h-full bg-white" data-id="element-564" />;
    if (slot.status === 'booked' && slot.brandName) {
      // Brand privacy: if viewing as a brand, mask other brands
      if (isBrandView && slot.brandName !== brandName) {
        return <div className="w-full h-full flex items-center justify-center px-1 py-0.5 font-medium text-[10px] leading-tight bg-gray-100 text-gray-400" title="Unavailable" data-id="element-565">
            Booked
          </div>;
      }
      const colorClass = getBrandColorClass(slot.brandName);
      return <div className={`w-full h-full flex items-center justify-center px-1 py-0.5 font-bold text-[10px] leading-tight ${isBrandView ? 'bg-primary/20 text-primary' : colorClass}`} title={`${slot.brandName} - ${slot.positionName || ''}`} data-id="element-566">
          {isBrandView ? 'Your Booking' : slot.brandName}
        </div>;
    }
    if (slot.status === 'internal') {
      if (isBrandView) {
        return <div className="w-full h-full flex items-center justify-center px-1 py-0.5 font-medium text-[10px] leading-tight bg-gray-100 text-gray-400" title="Unavailable" data-id="element-567">
            Booked
          </div>;
      }
      return <div className="w-full h-full flex items-center justify-center px-1 py-0.5 font-bold text-[10px] leading-tight bg-amber-100 text-amber-800" data-id="element-568">
          WW
        </div>;
    }
    if (slot.status === 'blocked') {
      return <div className={`w-full h-full ${isBrandView ? 'bg-gray-100' : 'bg-gray-200'}`} title={isBrandView ? 'Unavailable' : 'Blocked'} data-id="element-569" />;
    }
    if (slot.status === 'removed') {
      return <div className="w-full h-full bg-gray-100" title="Removed" data-id="element-570" />;
    }
    // Available — highlight for brand view
    if (isBrandView) {
      return <div className="w-full h-full bg-emerald-50" title="Available" data-id="element-571">
          <div className="w-full h-full flex items-center justify-center text-[9px] font-medium text-emerald-600" data-id="element-572">
            Open
          </div>
        </div>;
    }
    return <div className="w-full h-full bg-white" data-id="element-573" />;
  };
  return <div className="flex flex-col h-full bg-white w-full" data-id="element-574">
      {/* Header & Toggle */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 flex-shrink-0" data-id="element-575">
        <div data-id="element-576">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-577">
            {isBrandView ? 'Store Availability' : 'Booking Calendar'}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5" data-id="element-578">
            {isBrandView ? 'Browse available positions across all Woolworths stores' : 'Spreadsheet overview — screens & drive aisles per store'}
          </p>
        </div>

        <div className="flex items-center gap-4" data-id="element-579">
          {viewMode === 'week' ? <div className="relative" data-id="element-580">
              <select value={selectedWeek} onChange={e => setSelectedWeek(Number(e.target.value))} className="appearance-none bg-white border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-581">
                {weeks.map(w => <option key={w.weekNumber} value={w.weekNumber} data-id="element-582">
                    Week {w.weekNumber} ({formatDate(w.start)} -{' '}
                    {formatDate(w.end)})
                  </option>)}
              </select>
              <ChevronDownIcon className="w-4 h-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-583" />
            </div> : !isBrandView && <div className="relative" data-id="element-584">
                <select value={selectedBrand} onChange={e => setSelectedBrand(e.target.value)} className="appearance-none bg-white border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-585">
                  {allBrands.map(brand => <option key={brand} value={brand} data-id="element-586">
                      {brand}
                    </option>)}
                </select>
                <ChevronDownIcon className="w-4 h-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-587" />
              </div>}

          {!isBrandView && <div className="flex items-center p-1 bg-gray-100 border border-gray-200 rounded-lg" data-id="element-588">
              <button onClick={() => setViewMode('week')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${viewMode === 'week' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-589">
                <CalendarIcon className="w-4 h-4" data-id="element-590" />
                By Week
              </button>
              <button onClick={() => setViewMode('brand')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${viewMode === 'brand' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-591">
                <UsersIcon className="w-4 h-4" data-id="element-592" />
                By Brand
              </button>
            </div>}

          {isBrandView && <div className="flex items-center gap-3 text-xs text-gray-500" data-id="element-593">
              <span className="flex items-center gap-1.5" data-id="element-594">
                <span className="w-3 h-3 rounded-sm bg-emerald-50 border border-emerald-200" data-id="element-595" />
                Available
              </span>
              <span className="flex items-center gap-1.5" data-id="element-596">
                <span className="w-3 h-3 rounded-sm bg-primary/20 border border-primary/30" data-id="element-597" />
                Your Booking
              </span>
              <span className="flex items-center gap-1.5" data-id="element-598">
                <span className="w-3 h-3 rounded-sm bg-gray-100 border border-gray-200" data-id="element-599" />
                Unavailable
              </span>
            </div>}
        </div>
      </div>

      {/* Buyer Legend */}
      {!isBrandView && <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0" data-id="element-600">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-601">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-602">
              Buyers:
            </span>
            {buyers.map(buyer => <div key={buyer.name} className="flex items-center gap-1.5" data-id="element-603">
                <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{
            backgroundColor: buyer.hex
          }} data-id="element-604" />
                <span className="text-[11px] font-medium text-gray-700" data-id="element-605">
                  {buyer.name}
                </span>
              </div>)}
            <div className="flex items-center gap-1.5" data-id="element-606">
              <div className="w-3 h-3 rounded-sm flex-shrink-0 bg-gray-300" data-id="element-607" />
              <span className="text-[11px] font-medium text-gray-700" data-id="element-608">
                Other
              </span>
            </div>
          </div>
        </div>}

      {/* Calendar Grid */}
      <div className="flex-1 overflow-auto bg-gray-50 p-4" data-id="element-609">
        <div className="bg-white border border-gray-300 rounded-lg shadow-sm inline-block min-w-full" data-id="element-610">
          {viewMode === 'week' && currentWeekData ? <table className="w-full text-left border-collapse" data-id="element-611">
              <thead data-id="element-612">
                {/* Top Header Row */}
                <tr data-id="element-613">
                  <th className="w-44 p-0 bg-white border-r border-gray-300 sticky left-0 z-10 rounded-tl-lg" data-id="element-614"></th>
                  <th colSpan={genericColumns.length} className="bg-gray-900 text-white py-2 px-4 text-center font-bold text-sm border-b-2 border-red-500 tracking-wider rounded-tr-lg" data-id="element-615">
                    {currentWeekData.month.split(' ')[0]} — WEEK{' '}
                    {currentWeekData.weekNumber} (
                    {formatDate(currentWeekData.start)}–
                    {formatDate(currentWeekData.end)})
                  </th>
                </tr>
                {/* Position Column Headers */}
                <tr data-id="element-616">
                  <th className="w-44 p-0 bg-gray-50 border-r border-b border-gray-300 sticky left-0 z-10" data-id="element-617">
                    <div className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-618">
                      Store
                    </div>
                  </th>
                  {genericColumns.map(col => <th key={col.key} className={`w-32 p-0 border-r border-b border-gray-300 ${POSITION_HEADER_COLORS[col.type]}`} data-id="element-619">
                      <div className="px-1.5 py-2 text-[10px] leading-tight font-bold text-center uppercase" data-id="element-620">
                        {col.label}
                      </div>
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-621">
                {TIER_ORDER.map(tier => {
              const tierStores = storesByTier[tier];
              if (tierStores.length === 0) return null;
              return <Fragment key={tier} data-id="element-622">
                      {/* Tier separator row */}
                      <tr data-id="element-623">
                        <td colSpan={genericColumns.length + 1} className="bg-gray-100 border-b border-gray-300 sticky left-0" data-id="element-624">
                          <div className="px-3 py-1 text-[10px] font-bold text-gray-500 uppercase tracking-widest" data-id="element-625">
                            {TIER_LABELS[tier]}
                          </div>
                        </td>
                      </tr>
                      {tierStores.map(store => <tr key={store.id} className="hover:bg-gray-50/50" data-id="element-626">
                          <td className="p-0 bg-white border-r border-b border-gray-200 sticky left-0 z-10" data-id="element-627">
                            <div className="px-3 py-1.5" data-id="element-628">
                              <div className="text-[11px] font-semibold text-gray-900 truncate" title={store.name} data-id="element-629">
                                {store.name}
                              </div>
                              <div className="text-[9px] text-gray-400 truncate" data-id="element-630">
                                {store.province}
                              </div>
                            </div>
                          </td>
                          {genericColumns.map(col => {
                    const {
                      slot,
                      positionName
                    } = getStoreSlotForColumn(store, col.key, selectedWeek);
                    if (!positionName) {
                      return <td key={col.key} className="p-0 border-r border-b border-gray-200 bg-gray-50/50 h-10" title="No position" data-id="element-631">
                                  <div className="w-full h-full flex items-center justify-center" data-id="element-632">
                                    <span className="text-[10px] text-gray-300" data-id="element-633">
                                      –
                                    </span>
                                  </div>
                                </td>;
                    }
                    return <td key={col.key} className="p-0 border-r border-b border-gray-200 h-10" title={positionName} data-id="element-634">
                                {renderCell(slot)}
                              </td>;
                  })}
                        </tr>)}
                    </Fragment>;
            })}
              </tbody>
            </table> : viewMode === 'brand' ? <table className="w-full text-left border-collapse" data-id="element-635">
              <thead data-id="element-636">
                {/* Top Header Row */}
                <tr data-id="element-637">
                  <th className="w-44 p-0 bg-white border-r border-gray-300 sticky left-0 z-10 rounded-tl-lg" data-id="element-638"></th>
                  <th colSpan={weeks.length} className="bg-gray-900 text-white py-2 px-4 text-center font-bold text-sm border-b-2 border-red-500 tracking-wider uppercase rounded-tr-lg" data-id="element-639">
                    BRAND BOOKINGS: {selectedBrand}
                  </th>
                </tr>
                {/* Weeks Header Row */}
                <tr data-id="element-640">
                  <th className="w-44 p-0 bg-gray-50 border-r border-b border-gray-300 sticky left-0 z-10" data-id="element-641">
                    <div className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-642">
                      Store
                    </div>
                  </th>
                  {weeks.map(w => <th key={w.weekNumber} className="w-28 p-0 border-r border-b border-gray-300 bg-gray-100" data-id="element-643">
                      <div className="px-2 py-2 text-center" data-id="element-644">
                        <div className="text-[11px] font-bold text-gray-700 uppercase" data-id="element-645">
                          Wk {w.weekNumber}
                        </div>
                        <div className="text-[9px] text-gray-500 font-normal mt-0.5" data-id="element-646">
                          {formatDate(w.start)}
                        </div>
                      </div>
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-647">
                {TIER_ORDER.map(tier => {
              const tierStores = storesByTier[tier];
              if (tierStores.length === 0) return null;
              const brandStores = tierStores.filter(store => store.slots.some(s => s.status === 'booked' && s.brandName === selectedBrand));
              if (brandStores.length === 0) return null;
              return <Fragment key={tier} data-id="element-648">
                      {/* Tier separator row */}
                      <tr data-id="element-649">
                        <td colSpan={weeks.length + 1} className="bg-gray-100 border-b border-gray-300 sticky left-0" data-id="element-650">
                          <div className="px-3 py-1 text-[10px] font-bold text-gray-500 uppercase tracking-widest" data-id="element-651">
                            {TIER_LABELS[tier]}
                          </div>
                        </td>
                      </tr>
                      {brandStores.map(store => <tr key={store.id} className="hover:bg-gray-50/50" data-id="element-652">
                          <td className="p-0 bg-white border-r border-b border-gray-200 sticky left-0 z-10" data-id="element-653">
                            <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-900 truncate" title={store.name} data-id="element-654">
                              {store.name}
                            </div>
                          </td>
                          {weeks.map(w => {
                    const brandSlots = store.slots.filter(s => s.weekNumber === w.weekNumber && s.status === 'booked' && s.brandName === selectedBrand);
                    return <td key={w.weekNumber} className="p-0 border-r border-b border-gray-200 h-7 align-top bg-white" data-id="element-655">
                                {brandSlots.length > 0 ? <div className="flex flex-col h-full" data-id="element-656">
                                    {brandSlots.map(slot => {
                          const storePos = store.positions?.find(p => p.id === slot.positionId);
                          const colorClass = storePos ? POSITION_BADGE_COLORS[storePos.type] : 'bg-gray-100 text-gray-600';
                          return <div key={slot.id} className={`flex-1 flex items-center justify-center px-1 py-0.5 text-[9px] font-bold text-center border-b border-gray-100 last:border-b-0 ${colorClass}`} data-id="element-657">
                                          {slot.positionName?.toUpperCase() || 'UNKNOWN'}
                                        </div>;
                        })}
                                  </div> : <div className="w-full h-full" data-id="element-658" />}
                              </td>;
                  })}
                        </tr>)}
                    </Fragment>;
            })}
                {TIER_ORDER.every(tier => !storesByTier[tier].some(store => store.slots.some(s => s.status === 'booked' && s.brandName === selectedBrand))) && <tr data-id="element-659">
                    <td colSpan={weeks.length + 1} className="px-6 py-12 text-center text-gray-400 font-medium border-b border-gray-200" data-id="element-660">
                      No bookings found for {selectedBrand}.
                    </td>
                  </tr>}
              </tbody>
            </table> : null}
        </div>
      </div>
    </div>;
}